import React from 'react'

import { Stack, Typography } from '@mui/material'

import { Modal, Button } from '@repo/ui'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'

import { useRemoveAuthFactorMutation, type AuthFactor } from 'hooks/api/useAuthFactors'

interface RemoveFactorModalProps {
  factor: AuthFactor | null
  open: boolean
  onClose: () => void
}

/**
 * §32/§38 — removing a factor destroys the `user_auth_factors` row, so it's
 * a Trash action with a confirm modal, mirroring `DeleteAddressModal`
 * exactly (loud, always-red confirm button; the persistent trigger on the
 * row stays neutral-until-hover per `AuthFactorRow`).
 */
export const RemoveFactorModal = ({ factor, open, onClose }: RemoveFactorModalProps) => {
  const { open: notification } = useSnackbar()
  const { removeAuthFactorAsync, isRemovingAuthFactor } = useRemoveAuthFactorMutation()

  const label = factor?.label || (factor?.type === 'totp' ? 'authenticator app' : 'text message')

  const handleRemove = async () => {
    if (!factor) return
    try {
      await removeAuthFactorAsync({ factorId: factor.id })
      notification({ type: 'success', title: 'Removed', text: `${label} was removed from your account.` })
      onClose()
    } catch (error) {
      notification({ type: 'error', title: 'Could not remove', text: getErrorMessage(error) })
    }
  }

  return (
    <Modal
      title="Remove two-factor method"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button disableElevation variant="secondary" onClick={onClose} disabled={isRemovingAuthFactor}>
            Cancel
          </Button>
          <Button disableElevation destructive loading={isRemovingAuthFactor} onClick={handleRemove}>
            Remove
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {`Are you sure you want to remove "${label}" as a two-factor method? You'll no longer be able to use it to sign in.`}
        </Typography>
      </Stack>
    </Modal>
  )
}
